import React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Pagination from '@mui/material/Pagination';
import { NavLink } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import { useContext, useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import FormControl from '@mui/material/FormControl';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

export default function TaskDefinitions() {
  const [taskDefinitionList, setTaskDefinitionList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchText, setSearchText] = useState('');

  const authCtx = useContext(AuthContext);
  const debounceRef = useRef();

  // Fetch task definitions
  const fetchTaskDefinitions = async (searchVal = searchText, pageVal = page) => {
    const payload = {
      search: searchVal,
      page: pageVal,
      limit: 10,
    };

    setLoading(true);
    try {
      const response = await postRequest('private/taskDefinitionList', payload, authCtx.token);
      if (response?.data) {
        setTaskDefinitionList(response.data.docs || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalDocs(response.data.totalDocs || 0);
      } else {
        setTaskDefinitionList([]);
        setTotalPages(1);
        setTotalDocs(0);
      }
    } catch (err) {
      console.error('Error fetching task definitions:', err);
      setTaskDefinitionList([]);
      setTotalPages(1);
      setTotalDocs(0);
    } finally {
      setLoading(false);
    }
  };

  // On initial load and page change
  useEffect(() => {
    fetchTaskDefinitions(searchText, page);
    // eslint-disable-next-line
  }, [page]);

  // Debounced search on text change
  useEffect(() => {
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchTaskDefinitions(searchText, 1);
    }, 400);
    // eslint-disable-next-line
  }, [searchText]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handleDelete = async (taskDefinitionId) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to delete this task definition?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (confirm.isConfirmed) {
      try {
        const response = await postRequest(`private/deleteTaskDefinition/${taskDefinitionId}`, {}, authCtx.token);
        if (response?.status === 200) {
          Swal.fire('Deleted!', 'Task definition has been deleted.', 'success');
          fetchTaskDefinitions(searchText, page);
        } else {
          Swal.fire('Error', response?.message || 'Failed to delete task definition', 'error');
        }
      } catch (err) {
        Swal.fire('Error', err?.message || 'Failed to delete task definition', 'error');
      }
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Task Definitions', path: '' },
        ]}
      />
      <br />
      
      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>Task Definitions</h1>
        <div className='header-right-btn'>
          <Stack spacing={2} direction="row">
            <Button variant="outlined" className='red-outline-btn text-capitalize'>
              <NavLink to="/system-setup/taskdefinitions/create" className="red-link-text">
                <AddIcon /> &nbsp; Create New Task Definition
              </NavLink>
            </Button>
          </Stack>
        </div>
      </div>

      {/* Search Form */}
      <div className='page-top-formview mb-3'>
        <div style={{ 
          backgroundColor: '#fff', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <Row className="mb-3">
            <Col xs={12}>
              <Form.Group controlId="search">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <TextField
                    id="search-field"
                    label="Search by name"
                    variant="outlined"
                    value={searchText}
                    onChange={handleSearchChange}
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                      },
                    }}
                  />
                </FormControl>
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>

      {/* Table View */}
      <div className='page-top-formview mb-3'>
        <div style={{ 
          backgroundColor: '#fff', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <Table striped responsive bordered>
                <thead>
                  <tr>
                    <th colSpan={6} className='bg-grey'>Task Definitions</th>
                  </tr>
                  <tr>
                    <th>Product</th>
                    <th>Name</th>
                    <th>Apply Target</th>
                    <th>Target</th>
                    <th>Due Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {taskDefinitionList.length > 0 ? (
                    taskDefinitionList.map((taskDef) => (
                      <tr key={taskDef._id}>
                        <td>{taskDef.product_id?.title || 'N/A'}</td>
                        <td>{taskDef.name}</td>
                        <td style={{ textTransform: 'capitalize' }}>
                          {taskDef.apply_target_type === 'userGroup' ? 'User Group' : 'User'}
                        </td>
                        <td>
                          {taskDef.apply_target_type === 'userGroup' 
                            ? taskDef.target_user_group?.title || 'N/A'
                            : `${taskDef.target_user?.forename || ''} ${taskDef.target_user?.surname || ''}`.trim() || 'N/A'
                          }
                        </td>
                        <td>
                          {taskDef.completion_due_num 
                            ? `${taskDef.completion_due_num} ${taskDef.completion_due_unit}`
                            : 'N/A'
                          }
                        </td>
                        <td>
                          <Tooltip title="Edit Task Definition">
                            <NavLink 
                              to={`/system-setup/taskdefinitions/edit/${taskDef._id}`} 
                              className="red-link-text"
                            >
                              <EditOutlinedIcon />
                            </NavLink>
                          </Tooltip>
                          &nbsp;&nbsp;&nbsp;
                          <span
                            className='red-link-text cursor-pointer'
                            onClick={() => handleDelete(taskDef._id)}
                            title="Delete"
                          >
                            <Tooltip title="Delete Task Definition">
                              <DeleteOutlineOutlinedIcon />
                            </Tooltip>
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                        No task definitions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Pagination */}
              {taskDefinitionList.length > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8, marginBottom: 8 }}>
                    <span className="text-muted small">
                      Showing {((page - 1) * 10) + 1}-{Math.min(page * 10, totalDocs)} of {totalDocs} task definitions
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      size="medium"
                      sx={{
                        '& .Mui-selected': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                          color: '#fff !important',
                        },
                        '& .MuiPaginationItem-root': {
                          color: 'var(--bs-red-color)',
                          borderColor: 'var(--bs-red-color)',
                        },
                      }}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}